import { amplitudeLogSaasEvent, promiseWrapper, server, trackMpEvent } from '@/helpers';
import { PLACE_IMPRESSION, placeService } from '@/services';
import { getPopularAndSimilar } from '@/services/searchService';
import { Place } from '@/types/state/shared';
import * as Sentry from '@sentry/react';
import { useEffect, useReducer } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { EVENT_NAME, SCREEN_NAME, UNVERIFIED_SUBSCRIPTION_TYPE } from './constants/tracking';
import { getCompanyTrackingProps, sendStartedSignupFlowTracking } from './helpers/tracking';
import { profilePageReducer, ProfilePageState } from './ProfilePage.reducer';
import { StartingPoint } from './types/tracking';

export const useProfilePage = (place: Place) => {
  const initialState: ProfilePageState = {
    popularPlaces: [],
    similarPlaces: [],
    lastVisitedPlaces: [],
    showPopup: false,
    showVerifyModal: { show: false },
  };

  const [state, dispatch] = useReducer(profilePageReducer, initialState);
  const isPremium = place.about?.settings?.subscriptionType === 'profilepagepremium';
  const isVerified = place.isVerifiedExternalPlace;
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showPopupQuery = !isVerified && queryParams.get('sp') === '1';

  const handleCloseNewCustomerModal = () => {
    dispatch({ type: 'SET_SHOW_POPUP', payload: false });
  };

  const handleVerifyCompanyClick = (initiatingPoint: StartingPoint) => {
    sendStartedSignupFlowTracking(initiatingPoint, place);
    dispatch({ type: 'SET_SHOW_VERIFY_MODAL', payload: { show: true, trigger: initiatingPoint } });
  };

  const handleCloseVerifyModal = () => {
    dispatch({ type: 'SET_SHOW_VERIFY_MODAL', payload: { show: false, trigger: undefined } });
  };

  useEffect(() => {
    const getPopularAndSimilarPlaces = async () => {
      const { data, error } = await promiseWrapper(getPopularAndSimilar(place.id.toString()));

      if (data?.popular?.length > 0) {
        dispatch({
          type: 'SET_POPULAR_PLACES',
          payload: data.popular.map((place) => {
            return {
              id: place.id,
              to: `/places/${place.about?.slug}-${place.id}`,
              name: place.name,
              address: { street: place.address?.street, city: place.address?.city },
              rating: place.rating?.score,
            };
          }),
        });
      }

      if (data?.similar?.length > 0) {
        dispatch({
          type: 'SET_SIMILAR_PLACES',
          payload: data.similar.map((place) => {
            return {
              id: place.id,
              to: `/places/${place.about?.slug}-${place.id}`,
              name: place.name,
              address: { street: place.address?.street, city: place.address?.city },
              rating: place.rating?.score,
            };
          }),
        });
      }

      if (error) {
        Sentry.captureException(error);
      }
    };

    if (!isPremium) {
      getPopularAndSimilarPlaces();
    }

    if (!place.isVerifiedExternalPlace) {
      trackMpEvent(EVENT_NAME.COMPANY_VIEWED, {
        ...getCompanyTrackingProps({
          id: place.id,
          name: place.about?.name,
          screen: SCREEN_NAME.COMPANY_DETAILS,
          subType: UNVERIFIED_SUBSCRIPTION_TYPE,
        }),
        company_id: String(place.id),
      });
      amplitudeLogSaasEvent(EVENT_NAME.COMPANY_VIEWED, {
        ...getCompanyTrackingProps(
          {
            id: place.id,
            name: place.about?.name,
            screen: SCREEN_NAME.COMPANY_DETAILS,
            subType: UNVERIFIED_SUBSCRIPTION_TYPE,
          },
          true,
        ),
      });
    }

    const fetchRecentVisistedUserPlaces = async () => {
      const lastVisitedPlaces = JSON.parse(localStorage.getItem('mpLastVisitedPlaces') || '[]');
      const { data, error } = await promiseWrapper(
        server.request.get('/places/recent', {
          params: { ids: lastVisitedPlaces.join(',') },
        }),
      );

      if (error) {
        Sentry.captureException(error);
      }

      if (data?.data?.length > 0) {
        dispatch({
          type: 'SET_LAST_VISITED_PLACES',
          payload: data.data.map((place) => ({
            id: place.id,
            to: `/places/${place.about?.slug}-${place.id}`,
            name: place.about?.name,
            rating: place.rating?.score,
            address: { street: place.contact?.address?.street, city: place.contact?.address?.city },
          })),
        });
      }
    };

    fetchRecentVisistedUserPlaces();

    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_IMPRESSION);

    if (showPopupQuery) {
      history.replace({
        pathname: location.pathname,
        search: '',
      });
      dispatch({ type: 'SET_SHOW_POPUP', payload: true });
    }
  }, []);

  return {
    popularPlaces: state.popularPlaces,
    similarPlaces: state.similarPlaces,
    lastVisitedPlaces: state.lastVisitedPlaces,
    showPopup: state.showPopup,
    handleCloseNewCustomerModal,
    showVerifyModal: state.showVerifyModal,
    handleVerifyCompanyClick,
    handleCloseVerifyModal,
  };
};
